<template>
  <div
    ref="rootEl"
    class="text-textPrimary text-left bg-white"
    data-testid="ds-attendance-activity-audit"
  >
    <!-- Carregando o registro: o corpo inteiro só aparece com dado real (mesma regra do BUG-PP-02). -->
    <div
      v-if="isLoadingDetail"
      class="flex items-center justify-center py-16"
      data-testid="activity-audit-loading"
    >
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div
      v-else-if="hasLoadError"
      data-testid="activity-audit-error"
      role="alert"
      aria-live="assertive"
      class="flex flex-col items-center justify-center gap-4 py-16 text-center"
    >
      <v-icon size="48" color="error">{{ mdiAlertCircleOutline }}</v-icon>
      <div>
        <p class="text-lg font-bold text-textPrimary">
          Não foi possível carregar o Registro de Atividade.
        </p>
        <p v-if="loadErrorMessage" class="mt-1 text-sm text-gray-500">{{ loadErrorMessage }}</p>
      </div>
      <OutlineButton
        text="Voltar para Aprovações"
        width="200px"
        height="40px"
        data-testid="activity-audit-back-button"
        @click="goToApprovals"
      />
    </div>

    <div v-else class="flex flex-col gap-4 mt-4">
      <!-- ---------------------------------------------------------------- Cabeçalho -->
      <div
        data-testid="activity-audit-header"
        class="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-lightGray30 bg-lightGray30/30 px-3 py-2"
      >
        <span class="rounded-lg border border-lightGray30 bg-white px-2 py-1 text-sm">
          #{{ containerId }}
        </span>
        <span class="text-base font-bold text-textPrimary">{{ headerTitle }}</span>

        <div class="flex flex-wrap items-center gap-2">
          <StatusBadge v-if="currentStatus" :status="currentStatus" />
          <LabelBadge
            v-for="flag in riskFlagLabels"
            :key="flag"
            data-testid="activity-audit-risk-chip"
            :label="flag"
            color="orange"
            size="small"
          />
          <LabelBadge
            v-if="detail?.legacyMigrated"
            data-testid="activity-audit-legacy-chip"
            label="Migrado da Avaliação de Evidências"
            color="gray"
            size="small"
          />
        </div>

        <dl class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral50">
          <div v-for="fact in headerFacts" :key="fact.label" class="flex items-baseline gap-1">
            <dt class="font-bold text-textPrimary">{{ fact.label }}:</dt>
            <dd>{{ fact.value }}</dd>
          </div>
        </dl>

        <OutlineButton
          v-if="returnToQueuePath"
          text="Voltar para a fila"
          width="180px"
          height="32px"
          class="ml-auto"
          data-testid="activity-audit-return-to-queue"
          @click="goToReturnToQueue"
        />
      </div>

      <!--
        M3-3 / Q28 — pós-plantão já aprovado. O efetivo está congelado, mas a aprovação
        CONTINUA possível: o registro entra marcado como recebido após fechamento e não
        altera o horário efetivo até alguém reabrir a avaliação do pós-plantão.
      -->
      <div
        v-if="isPostStationClosed"
        data-testid="activity-audit-after-closure-banner"
        class="flex flex-col gap-1 rounded-lg border border-alert bg-alert/10 p-3"
      >
        <span
          class="w-fit rounded bg-alert px-2 py-[2px] text-[11px] font-bold uppercase leading-4 tracking-wide text-white"
        >
          Requer atenção
        </span>
        <p class="text-sm leading-[18px] text-textPrimary">
          <span class="font-bold">Pós-plantão aprovado{{ frozenAtSuffix }}</span> — este plantão
          está fechado. A aprovação continua possível: o registro fica marcado como
          <span class="font-bold">recebido após fechamento</span> e não altera o horário efetivo
          enquanto a avaliação do pós-plantão não for reaberta.
        </p>
      </div>

      <div class="flex flex-col gap-4 lg:flex-row lg:items-start">
        <!-- ------------------------------------------------------- Visualizador do anexo -->
        <section
          class="flex w-full min-w-0 flex-col lg:w-3/5"
          data-testid="activity-audit-attachment-viewer"
        >
          <div class="mb-2 flex flex-wrap items-center gap-2 text-xs text-neutral50">
            <span class="font-bold text-textPrimary" data-testid="activity-audit-attachment-name">
              {{ attachmentCaption }}
            </span>
            <span class="ml-auto">
              Proxy autenticado com marca d'água — o mesmo mecanismo do anexo de check.
            </span>
          </div>

          <div
            v-if="isLoadingAttachment"
            class="flex items-center justify-center rounded-lg border border-lightGray30 bg-lightGray30"
            :style="{ height: `${VIEWER_HEIGHT_PX}px` }"
          >
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div
            v-else-if="!attachmentUrl"
            class="flex items-center justify-center rounded-lg border border-lightGray30 bg-lightGray30 text-sm text-neutral50"
            :style="{ height: `${VIEWER_HEIGHT_PX}px` }"
            data-testid="activity-audit-attachment-empty"
          >
            Nenhum anexo disponível para visualização.
          </div>
          <div
            v-else-if="viewerMode === 'other'"
            class="flex flex-col items-center justify-center gap-3 rounded-lg border border-lightGray30 bg-lightGray30 text-sm text-neutral50"
            :style="{ height: `${VIEWER_HEIGHT_PX}px` }"
            data-testid="activity-audit-attachment-download"
          >
            <v-icon size="40">{{ mdiTrayArrowDown }}</v-icon>
            <p>
              {{
                imageLoadFailed
                  ? 'Não foi possível exibir esta imagem.'
                  : 'Este tipo de anexo não pode ser exibido aqui.'
              }}
            </p>
            <OutlineButton
              text="Baixar anexo"
              width="200px"
              height="40px"
              data-testid="activity-audit-attachment-download-button"
              @click="downloadCurrentAttachment"
            />
          </div>
          <!--
            Reaproveitamento obrigatório (M3 balão ⑥): o MESMO `StepPreview` da folha, que já
            traz zoom, rotação, tela cheia e a lupa 2×. Nada de um segundo visualizador. Só
            entra para 'image' (mime real) e 'pdf' — "outro" mime e falha de carga da
            imagem caem no bloco de download acima (`viewerMode === 'other'`).
          -->
          <StepPreview
            v-else
            mode="preview"
            :file="null"
            :file-url="attachmentUrl"
            :file-type="attachmentType"
            :rotation="rotation"
            :zoom="zoom"
            :magnifier="isMagnifierOn"
            :magnifier-toggle="true"
            :height-px="VIEWER_HEIGHT_PX"
            :show-display-name="false"
            @update:rotation="rotation = $event"
            @update:zoom="zoom = $event"
            @update:magnifier="isMagnifierOn = $event"
            @image-error="handleAttachmentImageError"
          />

          <div v-if="attachments.length > 1" class="mt-2 flex flex-wrap items-center gap-2">
            <button
              v-for="(attachment, index) in attachments"
              :key="attachment.evidenceId"
              type="button"
              :data-testid="`activity-audit-attachment-tab-${index}`"
              :class="[
                'rounded-lg border px-3 py-1 text-xs',
                index === attachmentIndex
                  ? 'border-primary30 font-bold text-primary30'
                  : 'border-lightGray30 text-neutral50 hover:bg-gray-100'
              ]"
              @click="selectAttachment(index)"
            >
              {{ index + 1 }}. {{ attachment.originalFileName || 'anexo' }}
            </button>
          </div>
        </section>

        <!-- --------------------------------------------------------------- Painel de decisão -->
        <section class="flex w-full min-w-0 flex-col gap-4 lg:w-2/5">
          <!-- Dados do plantão -->
          <div
            class="rounded-lg border border-lightGray30 p-3"
            data-testid="activity-audit-station"
          >
            <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-neutral50">
              Dados do plantão
            </h4>
            <dl class="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
              <template v-for="row in stationRows" :key="row.label">
                <dt class="font-bold text-textPrimary">{{ row.label }}</dt>
                <dd class="text-neutral50">{{ row.value }}</dd>
              </template>
            </dl>
          </div>

          <!-- O que o sistema já tem -->
          <div
            class="rounded-lg border border-lightGray30 p-3"
            data-testid="activity-audit-effective-now"
          >
            <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-neutral50">
              O que o sistema já tem
              <span class="font-normal normal-case tracking-normal">por perna</span>
            </h4>
            <div
              v-for="leg in effectiveNowRows"
              :key="leg.key"
              class="flex items-baseline justify-between gap-2 border-b border-lightGray30 py-1 last:border-b-0"
              :data-testid="`activity-audit-effective-now-${leg.key}`"
            >
              <span class="text-sm">
                <b class="font-mono">{{ leg.value }}</b>
                {{ leg.label }}
              </span>
              <span class="text-xs text-neutral50">{{ leg.source }}</span>
            </div>
          </div>

          <!-- Declarado pelo emissor — IMUTÁVEL (§3.4 regra 1) -->
          <div
            class="rounded-lg border border-lightGray30 p-3"
            data-testid="activity-audit-declared"
          >
            <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-neutral50">
              Declarado pelo emissor
            </h4>
            <dl class="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
              <template v-for="row in declaredRows" :key="row.label">
                <dt class="font-bold text-textPrimary">{{ row.label }}</dt>
                <dd class="text-neutral50">{{ row.value }}</dd>
              </template>
            </dl>
            <p class="mt-2 text-xs text-neutral50" data-testid="activity-audit-timezone-legend">
              {{ timezoneLegend }}
            </p>
            <p class="mt-2 text-xs text-neutral50">
              Imutável. O que o auditor mudar entra no campo abaixo — os dois ficam gravados.
            </p>
          </div>

          <!-- Valor do auditor -->
          <div
            class="rounded-lg border border-lightGray30 p-3"
            data-testid="activity-audit-auditor"
          >
            <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-neutral50">
              Valor do auditor
            </h4>

            <div v-for="leg in auditorLegs" :key="leg.key" class="mb-3 flex flex-col gap-1">
              <label class="text-sm font-bold text-textPrimary" :for="`activity-audit-${leg.key}`">
                {{ leg.label }}<span v-if="leg.covered"> *</span>
              </label>
              <input
                v-if="leg.covered"
                :id="`activity-audit-${leg.key}`"
                :data-testid="`activity-audit-auditor-${leg.key}`"
                type="time"
                class="w-36 rounded-lg border border-lightGray30 px-2 py-1 font-mono text-sm disabled:bg-lightGray30 disabled:text-neutral50"
                :value="leg.key === 'checkin' ? auditorCheckin : auditorCheckout"
                :disabled="!canDecide"
                @input="onAuditorInput(leg.key, $event)"
              />
              <!--
                Perna não coberta: gravar valor nela é 400 `ACTIVITY_LEG_NOT_COVERED` no
                backend. Em vez de um campo que só produz erro, o auditor lê o que continua
                valendo e de onde vem.
              -->
              <span
                v-else
                class="text-sm text-neutral50"
                :data-testid="`activity-audit-not-covered-${leg.key}`"
              >
                {{ leg.notCoveredText }}
              </span>
              <span
                v-if="leg.covered && leg.changed"
                class="text-xs text-alert"
                aria-live="polite"
                :data-testid="`activity-audit-changed-${leg.key}`"
              >
                alterado de {{ leg.declared || '--:--' }}
              </span>
            </div>

            <div v-if="auditorChangedDeclared" class="flex flex-col gap-1">
              <label class="text-sm font-bold text-textPrimary" for="activity-audit-reason">
                Motivo da alteração *
              </label>
              <textarea
                id="activity-audit-reason"
                v-model="overrideReason"
                data-testid="activity-audit-override-reason"
                rows="2"
                :disabled="!canDecide"
                aria-describedby="activity-audit-reason-hint"
                :aria-invalid="String(isOverrideReasonMissing)"
                class="w-full rounded-lg border border-lightGray30 px-2 py-1 text-sm disabled:bg-lightGray30"
              ></textarea>
              <span id="activity-audit-reason-hint" class="text-xs text-neutral50">
                Obrigatório quando o auditor altera o declarado (mínimo
                {{ ACTIVITY_DESCRIPTION_MIN_LENGTH }} caracteres). Sem motivo, "Aprovar" fica
                desabilitado.
              </span>
            </div>

            <div v-if="canDecide" class="mt-3 flex flex-col gap-1">
              <label class="text-sm font-bold text-textPrimary" for="activity-audit-observation">
                Observação do auditor
              </label>
              <textarea
                id="activity-audit-observation"
                v-model="auditorObservation"
                data-testid="activity-audit-observation"
                rows="2"
                class="w-full rounded-lg border border-lightGray30 px-2 py-1 text-sm"
                placeholder="opcional"
              ></textarea>
            </div>
          </div>

          <!-- Horário efetivo resultante + ranking aplicável -->
          <div
            v-if="effectivePreview"
            class="rounded-lg border border-lightGray30 p-3"
            data-testid="activity-audit-effective-preview"
          >
            <h4 class="mb-2 text-sm font-bold uppercase tracking-wide text-neutral50">
              Horário efetivo resultante
              <span class="font-normal normal-case tracking-normal">prévia da aprovação</span>
            </h4>

            <div
              v-for="leg in previewRows"
              :key="leg.key"
              class="border-b border-lightGray30 py-1 last:border-b-0"
              :data-testid="`activity-audit-preview-${leg.key}`"
              :data-changed="String(leg.changed)"
            >
              <div class="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span>{{ leg.label }}</span>
                <span class="flex items-baseline gap-2">
                  <span class="font-mono text-neutral50">{{ leg.beforeValue }}</span>
                  <span aria-hidden="true">→</span>
                  <b
                    class="font-mono"
                    :class="leg.changed ? 'text-primary30' : 'text-textPrimary'"
                    >{{ leg.afterValue }}</b
                  >
                </span>
              </div>
              <div class="text-xs text-neutral50">
                antes: {{ leg.beforeSource }} · depois: {{ leg.afterSource }}
              </div>
            </div>

            <div class="mt-2 flex items-baseline justify-between gap-2 text-sm">
              <span>Estado do plantão</span>
              <span class="font-bold" data-testid="activity-audit-preview-status">
                {{ previewRegistrationStatusLabel }}
              </span>
            </div>

            <p
              v-if="previewAfterClosure"
              class="mt-2 text-xs text-alert"
              data-testid="activity-audit-preview-after-closure"
            >
              O efetivo deste plantão está congelado: o registro entra marcado como
              <b>recebido após fechamento</b> e não muda o horário efetivo enquanto o pós-plantão
              não for reaberto.
            </p>

            <h5 class="mb-1 mt-3 text-xs font-bold uppercase tracking-wide text-neutral50">
              Ranking aplicável
            </h5>
            <div
              v-for="item in precedenceRanking"
              :key="item.kind"
              data-testid="activity-audit-precedence-item"
              :class="[
                'flex items-baseline gap-2 py-[2px] text-xs',
                item.wins ? 'font-bold text-textPrimary' : 'text-neutral50'
              ]"
            >
              <span class="font-mono">{{ item.rank }}</span>
              <span>{{ item.label }}</span>
              <span v-if="item.wins" class="text-neutral50">— {{ item.winsHint }}</span>
            </div>
            <p class="mt-2 text-xs text-neutral50">
              Empate no mesmo rank: vale o mais recente. O check cru não é apagado — o efetivo é uma
              tabela à parte.
            </p>
          </div>

          <!--
            I8 — a prévia é apoio, mas o sumiço silencioso dela é pior que a ausência: o
            auditor não tinha como distinguir "nada mudaria" de "a simulação não respondeu".
          -->
          <div
            v-else-if="isPreviewUnavailable"
            class="rounded-lg border border-dashed border-lightGray30 p-3"
            data-testid="activity-audit-preview-unavailable"
          >
            <h4 class="mb-1 text-sm font-bold uppercase tracking-wide text-neutral50">
              Horário efetivo resultante
            </h4>
            <p class="text-xs text-neutral50">
              Prévia indisponível — a simulação não respondeu. A decisão continua possível: o
              horário efetivo é recalculado na aprovação.
            </p>
          </div>

          <!--
            Veredito já dado: `APPROVED` já virou check e `REJECTED` já reabriu a solicitação
            para o emissor. Nos dois a tela é leitura — o motivo fica à vista no lugar onde os
            botões estavam. Voltar atrás é outra ação (reverter, abaixo), não um novo veredito
            por cima do antigo.
          -->
          <p
            v-if="isDecidableStatus && !canAuditAttendance"
            class="m-0 rounded-lg border border-lightGray30 bg-lightGray30/30 p-3 text-sm text-neutral50"
            role="status"
            data-testid="activity-audit-no-permission"
          >
            {{ NO_AUDIT_PERMISSION_MESSAGE }}
          </p>
          <div
            v-if="!isDecidableStatus"
            class="flex flex-col gap-1 rounded-lg border border-lightGray30 bg-lightGray30/30 p-3"
            data-testid="activity-audit-decision"
          >
            <h4 class="text-sm font-bold uppercase tracking-wide text-neutral50">
              {{ decisionTitle }}
            </h4>
            <p v-for="row in decisionRows" :key="row.label" class="text-sm text-textPrimary">
              <span class="font-bold">{{ row.label }}:</span> {{ row.value }}
            </p>
          </div>

          <!--
            Desfazer o veredito: leitura NÃO é o mesmo que definitivo. `APPROVED` e `REJECTED`
            voltam para a auditoria pelo mesmo endpoint da folha (`revert-validation`), sob a
            mesma permissão avançada. Até 01/10/2026 a reprovação de Registro de Atividade era
            terminal por produto (§3.6 / Q20); o PO removeu a regra — o backend cancela a
            solicitação de reenvio que a reprovação abriu, e recusa (409) quando o emissor já
            reenviou (ver `ACTIVITY_REJECTION_SUPERSEDED`).
          -->
          <div v-if="canRevertDecision" class="flex flex-wrap items-center gap-2">
            <FilledButton
              text="Reverter validação"
              class="bg-negative"
              width="180px"
              height="40px"
              data-testid="activity-audit-revert"
              :disabled="isSaving"
              @click="isRevertModalOpen = true"
            />
            <span class="text-xs text-neutral50" data-testid="activity-audit-revert-hint">
              {{ revertHint }}
            </span>
          </div>

          <!-- Ações -->
          <div
            v-if="canDecide"
            class="flex flex-wrap items-center gap-2"
            data-testid="activity-audit-actions"
          >
            <FilledButton
              v-if="hasNextContainer"
              text="Aprovar e abrir o próximo"
              class="bg-primary30"
              width="240px"
              height="40px"
              data-testid="activity-audit-approve-next"
              :disabled="!canApprove || isSaving"
              :title="approveBlockedReason"
              :aria-describedby="
                approveBlockedReason ? 'activity-audit-approve-blocked' : undefined
              "
              @click="approve({ openNext: true })"
            />
            <FilledButton
              text="Aprovar"
              class="bg-primary30"
              width="140px"
              height="40px"
              data-testid="activity-audit-approve"
              :disabled="!canApprove || isSaving"
              :title="approveBlockedReason"
              :aria-describedby="
                approveBlockedReason ? 'activity-audit-approve-blocked' : undefined
              "
              @click="approve({ openNext: false })"
            />
            <OutlineButton
              text="Rejeitar"
              width="140px"
              height="40px"
              data-testid="activity-audit-reject"
              :disabled="isSaving"
              :aria-expanded="String(isRejectOpen)"
              aria-controls="activity-audit-reject-panel"
              @click="isRejectOpen = !isRejectOpen"
            />
            <span class="text-xs text-neutral50">
              <kbd>Ctrl</kbd>+<kbd>Enter</kbd> aprova e abre o próximo.
            </span>
          </div>

          <!--
            I17 — o motivo do bloqueio vivia só no `title` de um botão DESABILITADO, que
            leitor de tela nenhum alcança (e mouse nenhum, porque `title` de `:disabled` não
            abre em vários navegadores). Vira texto, ligado aos botões por `aria-describedby`.
          -->
          <p
            v-if="canDecide && approveBlockedReason"
            id="activity-audit-approve-blocked"
            data-testid="activity-audit-approve-blocked"
            class="text-xs text-alert"
            aria-live="polite"
          >
            {{ approveBlockedReason }}
          </p>

          <!--
            Rejeição (Q20): o registro rejeitado não volta a ser editável e não há correção em
            cima dele — quem reenvia é o emissor, num registro novo que referencia este. Por
            isso não existe a ação de devolução que a folha tem. O que existe é o caminho de
            volta pelo Receptor Avançado: reverter a reprovação (acima), que recoloca ESTE
            registro na fila.
          -->
          <div
            v-if="isRejectOpen && canDecide"
            id="activity-audit-reject-panel"
            class="rounded-lg border border-negative p-3"
            data-testid="activity-audit-reject-panel"
            role="region"
            aria-label="Rejeitar registro — motivo obrigatório"
          >
            <h4
              ref="rejectPanelHeadingRef"
              tabindex="-1"
              class="mb-2 text-sm font-bold uppercase tracking-wide text-negative outline-none"
            >
              Rejeitar — motivo obrigatório
            </h4>
            <div role="radiogroup" aria-label="Motivo da rejeição">
              <div
                v-for="reason in rejectionReasonOptions"
                :key="reason.value"
                class="flex items-center gap-2 py-[2px]"
              >
                <input
                  :id="`activity-audit-reject-reason-${reason.value}`"
                  type="radio"
                  name="activity-audit-reject-reason"
                  :value="reason.value"
                  :checked="selectedRejectionReason === reason.value"
                  @change="selectedRejectionReason = reason.value"
                />
                <label
                  :for="`activity-audit-reject-reason-${reason.value}`"
                  :data-testid="`activity-audit-reject-reason-${reason.value}`"
                  class="text-sm"
                >
                  {{ reason.title }}
                </label>
              </div>
            </div>

            <textarea
              v-model="rejectionObservation"
              data-testid="activity-audit-reject-observation"
              rows="2"
              class="mt-2 w-full rounded-lg border border-lightGray30 px-2 py-1 text-sm"
              :placeholder="
                isOtherRejectionReason
                  ? `Observação obrigatória (mínimo ${ACTIVITY_DESCRIPTION_MIN_LENGTH} caracteres)`
                  : 'Observação (opcional)'
              "
            ></textarea>

            <p class="mt-2 text-xs text-neutral50">
              A reprovação reabre a solicitação para o emissor enviar um novo registro. Um Receptor
              Avançado pode reverter a reprovação.
            </p>

            <FilledButton
              text="Confirmar rejeição"
              aria-label="Confirmar rejeição do registro de atividade"
              class="mt-2 bg-negative"
              width="200px"
              height="40px"
              data-testid="activity-audit-reject-confirm"
              :disabled="!canReject || isSaving"
              @click="reject"
            />
          </div>

          <DsAttendanceHistoryCardSkeleton v-if="isLoadingHistory" />
          <DsAttendanceHistoryCard
            v-else
            title="Histórico do registro"
            :events="historyEvents"
            data-testid="activity-audit-history"
          />
        </section>
      </div>
    </div>

    <DsAttendanceRevertModal
      v-model="isRevertModalOpen"
      :saving="isSaving"
      @confirm="revertValidation"
    />

    <DsAttendanceLegOverwriteModal
      :model-value="isLegOverwriteOpen"
      :conflicts="legOverwriteConflicts"
      :effective-values="legOverwriteEffectiveValues"
      :effective-value-status="legOverwriteEffectiveValueStatus"
      :submitting="isLegOverwriteSubmitting"
      :error-message="legOverwriteError"
      :stale-notice="legOverwriteStale"
      @confirm="legOverwrite.confirm"
      @decisions-change="legOverwrite.refreshEffectiveValues"
      @cancel="legOverwrite.cancel"
    />
  </div>
</template>

<script setup lang="ts">
import {
  NO_AUDIT_PERMISSION_MESSAGE,
  useCanAuditAttendance
} from '../composables/useCanAuditAttendance'
import { useCanRevertValidation } from '../composables/useCanRevertValidation'
import {
  ACTIVITY_REJECTION_SUPERSEDED_FALLBACK_MESSAGE,
  isActivityRejectionSupersededError
} from '../errors'
import DsAttendanceRevertModal from './DsAttendanceRevertModal.vue'
import DsAttendanceLegOverwriteModal from './DsAttendanceLegOverwriteModal.vue'
import { useLegOverwriteApproval } from '../composables/useLegOverwriteApproval'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { mdiAlertCircleOutline, mdiTrayArrowDown } from '@mdi/js'
import { useToast } from 'vue-toastification'
import { useErrorToast } from '@/composables/useErrorToast'
import { getApiErrorMessage, hasApiErrorCode } from '@/utils/apiError'
import Network from '@/Network'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import StepPreview from '@/components/designSystem/modals/AttendanceUploadModal/StepPreview.vue'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import DsAttendanceHistoryCard from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import DsAttendanceHistoryCardSkeleton from '@/components/designSystem/Skeleton/DsAttendanceHistoryCardSkeleton.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import {
  ACTIVITY_DESCRIPTION_MIN_LENGTH,
  ACTIVITY_REJECTION_REASON_LABELS,
  ACTIVITY_RISK_FLAG_LABELS,
  ACTIVITY_TYPE_LABELS,
  ActivityRejectionReason,
  ActivityType,
  ContainerKind,
  COVERED_LEGS_LABELS,
  CoveredLegs,
  DS_ATTENDANCE_CONTAINER_ALREADY_IN_STATUS,
  DS_ATTENDANCE_CONTAINER_INVALID_TRANSITION,
  DSAttendanceStatus,
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  EFFECTIVE_SOURCE_KIND_LABELS,
  EffectiveRegistrationStatus,
  EffectiveSourceKind,
  type ActivityRiskFlag
} from '@/constants/ds-attendance.constants'
import type { EffectivePreview, EffectivePreviewLeg } from '@/Network/Services/DsAttendanceService'
import {
  buildActivityAuditDetail,
  isActivityAuditDetailShape,
  sourceKind,
  sourceServerLabel,
  type ActivityAuditAttachment,
  type ActivityAuditDeclared,
  type ActivityAuditDetail,
  type ActivityAuditDoctorStation,
  type ActivityAuditPolicy,
  type ActivityAuditSource,
  type ActivityContainerDetailSource,
  type ActivityListItemSource,
  type ActivityRegistrationSource
} from '../utils/activity-audit-detail'
import { classifyAttachmentViewer } from '../utils/activity-audit-attachment-viewer'
import { mapAuditTrailToHistoryEvents, type HistoryEvent } from '@/utils/dsAttendanceHistory'
import { toHHmm } from '../utils/activity-time'
import { DEFAULT_UTC_OFFSET, formatDateInOffset, normalizeUtcOffset } from '@/utils/date'
import { useNextContainer } from '../composables/useNextContainer'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'
import {
  PRIORITY_RETURN_TO_PARAM,
  isPriorityReturnTo
} from '@/modules/company/Approvals/PriorityPayments/actions'

/**
 * M3, Tela 2 — a estação de trabalho do Registro de Atividade.
 *
 * Por que um componente novo e não mais um ramo dentro de `DsAttendanceAwaitingAudit.vue`:
 * aquela tela tem ~2.500 linhas, é toda sobre uma FOLHA (espelho da escala, conciliação por
 * linha, templates) e nada disso existe aqui — um Registro de Atividade tem UM plantão, uma
 * entry e um punhado de anexos. O que as duas telas compartilham (visualizador com lupa,
 * "aprovar e abrir o próximo", histórico) é consumido dos mesmos módulos, não copiado.
 *
 * Tolerância a backend antigo é premissa da F2 inteira: o web deploya antes. Todo campo do
 * detalhe é opcional, `effective-preview` ausente só apaga o bloco da prévia, e nenhuma
 * ausência pode impedir aprovar ou rejeitar.
 */

const props = defineProps<{
  /** Aceita string porque o id vem de `route.params` na tela que delega para cá. */
  containerId: number | string
  /**
   * O detalhe que a tela hospedeira JÁ buscou para descobrir o `kind`. Quando vem, este
   * componente não repete o `getById` (I14). Continua opcional porque o componente também é
   * montado sozinho nos testes e pode ser roteado direto no futuro.
   */
  containerDetail?: unknown | null
}>()

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { notifyError } = useErrorToast()
const headerStore = useHeaderStore()
const { fetchNextId, goToNext } = useNextContainer()

const VIEWER_HEIGHT_PX = 620

/* ------------------------------------------------------------------------- *
 * Forma do detalhe: `ActivityAuditDetail` (utils/activity-audit-detail.ts), com
 * TUDO opcional. O backend não a serve numa rota só — `loadDetail` costura o
 * container achatado com activity-record, item da fila e entry (achado R2-C2).
 * ------------------------------------------------------------------------- */

type LegKey = 'checkin' | 'checkout'

const detail = ref<ActivityAuditDetail | null>(null)
const isLoadingDetail = ref(true)
const hasLoadError = ref(false)
const loadErrorMessage = ref<string | null>(null)

const historyEvents = ref<HistoryEvent[]>([])
const isLoadingHistory = ref(true)

const attachmentIndex = ref(0)
const attachmentUrl = ref<string | undefined>(undefined)
const isLoadingAttachment = ref(true)
/** `Content-Type` real da resposta do download — o `Blob` do fetch já vem com ele. */
const attachmentMimeType = ref<string | undefined>(undefined)
/** A `<img>` do `StepPreview` emitiu erro: nunca mostrar imagem quebrada, cai para download. */
const imageLoadFailed = ref(false)
const rotation = ref(0)
const zoom = ref(1)
const isMagnifierOn = ref(false)

const auditorCheckin = ref('')
const auditorCheckout = ref('')
const overrideReason = ref('')
/**
 * O que já está GRAVADO na entry (`auditor.checkin/checkout`), e não o declarado. Sem esta
 * referência, desfazer uma sobrescrita feita numa sessão anterior não gerava PATCH nenhum e
 * a tela aprovava um horário diferente do que exibia.
 */
const persistedCheckin = ref('')
const persistedCheckout = ref('')
const auditorObservation = ref('')

const isRejectOpen = ref(false)
const selectedRejectionReason = ref<string | null>(null)
const rejectionObservation = ref('')
const rejectPanelHeadingRef = ref<HTMLElement | null>(null)

/**
 * Achado r2-m4 (a11y): antes disto, abrir o painel de rejeição deixava o foco parado no
 * próprio botão "Rejeitar" — quem navega por teclado/leitor de tela não percebia que um
 * painel novo (com radiogroup obrigatório) tinha aparecido logo abaixo. Move o foco para o
 * título do painel (`tabindex="-1"`, não entra na ordem de tab normal) assim que ele abre.
 */
watch(isRejectOpen, (open) => {
  if (!open) return
  void nextTick(() => rejectPanelHeadingRef.value?.focus())
})

const isSaving = ref(false)
const isRevertModalOpen = ref(false)
const nextContainerId = ref<number | null>(null)
const rootEl = ref<HTMLElement | null>(null)

/* --------------------------------------------------------------- horários --------- */

/** Offset (ou `Z`) de um ISO. `T18:45:00-03:00` → `-03:00`. */
const ISO_OFFSET = /T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})/

/**
 * Offset de parede do PLANTÃO, lido das strings que o próprio backend mandou para ele.
 *
 * O PATCH da entry precisa de ISO 8601 COM offset (B17 §4.5): o `@Type(() => Date)` do DTO
 * aceita os dois, e sem offset o Node interpreta o valor como hora local DO SERVIDOR — o
 * horário gravado passaria a depender do fuso do processo. Derivar daqui, e não do
 * navegador, é o que impede um auditor em outro fuso de gravar um instante errado: é o
 * mesmo bug de fuso que a F1 documentou.
 *
 * Um candidato serializado em `Z` NÃO conta (carimbaria UTC numa hora de parede), e sem
 * nenhum offset numérico a régua é a mesma da tela inteira: `scheduleOffset` — nunca a
 * string vazia, que devolveria o valor ao fuso do processo (achado Opus MINOR-1).
 */
function stationOffset(): string {
  const candidates = [
    declared.value.checkin,
    declared.value.checkout,
    detail.value?.doctorStation?.expectedStart,
    detail.value?.doctorStation?.expectedEnd,
    detail.value?.effectiveNow?.checkin?.value,
    detail.value?.effectiveNow?.checkout?.value
  ]
  for (const candidate of candidates) {
    const match = candidate ? ISO_OFFSET.exec(String(candidate)) : null
    if (match && match[1] !== 'Z') return match[1].replace(/^([+-]\d{2})(\d{2})$/, '$1:$2')
  }
  return normalizeUtcOffset(scheduleOffset.value)
}

/**
 * Caminho inverso: devolve ao backend a MESMA string que o backend mandou para a perna, só
 * com a hora trocada — o que preserva a data original (plantão que vira o dia) e o offset
 * da unidade. A base é o declarado da perna; sem ele, o `expected` da MESMA perna na
 * registration (que já carrega a data certa do check-out num plantão noturno). Nunca a
 * `date` do container: é a `referenceDate`, errada para a perna que cai no dia seguinte
 * (achado Opus MINOR-7). Sem nenhuma das duas, fica vazio em vez de inventar uma data.
 */
function withTime(key: LegKey, hhmm: string): string | null {
  const original =
    key === 'checkin'
      ? (declared.value.checkin ?? detail.value?.doctorStation?.expectedStart)
      : (declared.value.checkout ?? detail.value?.doctorStation?.expectedEnd)
  if (!hhmm || !original || !/T\d{2}:\d{2}/.test(original)) return null
  const base = original.replace(/T\d{2}:\d{2}/, `T${hhmm}`)
  return ISO_OFFSET.test(base) ? base : `${base}${stationOffset()}`
}

function formatDate(value?: string | null): string {
  if (!value) return ''
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`)
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : format(parsed, 'dd/MM/yyyy', { locale: ptBR })
}

/* ------------------------------------------------------------- dados derivados ----- */

const NOT_INFORMED = 'Não informado'

const currentStatus = computed(() => (detail.value?.status as DSAttendanceStatus) ?? null)

/**
 * Trava por status (§3.6 / Q20). Só quem ainda está na fila DECIDE: `APPROVED` já virou check
 * no plantão e `REJECTED` já reabriu a solicitação para o emissor — aprovar ou reprovar de
 * novo por esta tela seria um veredito por cima do outro. A guarda mora no componente porque
 * as rotas `DsAttendancePendingAudit` e `DsAttendanceApproved` caem no MESMO host, que delega
 * para cá sem olhar o status.
 *
 * Decidir não é o mesmo que desfazer: `APPROVED` e `REJECTED` têm volta pelo
 * `revert-validation`, sob `attendance_advanced_receiving` (ver `canRevertDecision`). Um
 * registro reprovado só volta a ser decidível DEPOIS de revertido — por isso `REJECTED` fica
 * fora desta lista mesmo com a reversão disponível.
 *
 * Status AUSENTE (backend que não manda o campo) segue decidível: a trava existe para o
 * veredito que o backend afirma ter dado, não para a dúvida.
 */
const DECIDABLE_STATUSES: DSAttendanceStatus[] = [
  DSAttendanceStatus.PENDING_AUDIT,
  DSAttendanceStatus.AUDIT_IN_PROGRESS
]

/** O registro ainda aceita decisão (pendente/em auditoria), pelo status. */
const isDecidableStatus = computed(
  () => currentStatus.value === null || DECIDABLE_STATUSES.includes(currentStatus.value)
)

/** BUG-04: quem só envia abre a tela pelo "Ver registro", mas não decide (403). */
const canAuditAttendance = useCanAuditAttendance()

const canDecide = computed(() => isDecidableStatus.value && canAuditAttendance.value)

/**
 * Reverter é mais restrito que decidir: o `revert-validation` exige
 * `attendance_advanced_receiving`, não `attendance_basic_receiving`. Vale para os dois
 * vereditos — `APPROVED → AUDIT_IN_PROGRESS` e `REJECTED → PENDING_AUDIT` —, como na folha.
 * A reprovação de Registro de Atividade deixou de ser terminal em 01/10/2026 (decisão do PO).
 */
const canRevertValidation = useCanRevertValidation()

const REVERTIBLE_STATUSES: DSAttendanceStatus[] = [
  DSAttendanceStatus.APPROVED,
  DSAttendanceStatus.REJECTED
]

const canRevertDecision = computed(
  () =>
    currentStatus.value !== null &&
    REVERTIBLE_STATUSES.includes(currentStatus.value) &&
    canRevertValidation.value
)

/**
 * O que a reversão desfaz depende do veredito: a aprovação tinha criado um check no
 * plantão; a reprovação tinha aberto uma solicitação de reenvio para o emissor.
 */
const revertHint = computed(() =>
  currentStatus.value === DSAttendanceStatus.REJECTED
    ? 'O registro volta para a fila de auditoria e a solicitação de reenvio aberta pela reprovação é cancelada.'
    : 'O registro volta para auditoria e o check do plantão é desfeito.'
)

const declared = computed<ActivityAuditDeclared>(() => detail.value?.declared ?? {})

/**
 * Fuso da ESCALA — é nele que todo horário desta tela é lido e mostrado (decisão do
 * produto, 13/09). Nunca o do navegador (o mesmo registro mudaria de hora a cada
 * usuário) nem o do servidor (`TZ=America/Sao_Paulo` fazia 08:00 virar 11:00).
 */
const scheduleOffset = computed(() => detail.value?.doctorStation?.timezone || DEFAULT_UTC_OFFSET)

/** "horários em UTC-3, fuso da escala" — o rodapé que declara a régua da tela. */
const timezoneLegend = computed(
  () => `Horários em ${formatUtcOffsetLabelForLegend(scheduleOffset.value)}, fuso da escala.`
)

function formatUtcOffsetLabelForLegend(offset: string): string {
  const normalized = normalizeUtcOffset(offset)
  return `UTC${normalized}`
}

const declaredCheckin = computed(() => toHHmm(declared.value.checkin, scheduleOffset.value))
const declaredCheckout = computed(() => toHHmm(declared.value.checkout, scheduleOffset.value))

const activityTypeLabel = computed(() => {
  const code = declared.value.activityType
  if (code && code in ACTIVITY_TYPE_LABELS) {
    return ACTIVITY_TYPE_LABELS[code as ActivityType]
  }
  return declared.value.activityTypeLabel || NOT_INFORMED
})

const coveredLegs = computed<CoveredLegs>(
  () => (declared.value.coveredLegs as CoveredLegs) ?? CoveredLegs.BOTH
)

function isLegCovered(leg: LegKey): boolean {
  if (coveredLegs.value === CoveredLegs.BOTH) return true
  return coveredLegs.value === (leg === 'checkin' ? CoveredLegs.CHECKIN : CoveredLegs.CHECKOUT)
}

const doctorStation = computed<ActivityAuditDoctorStation>(() => detail.value?.doctorStation ?? {})

const headerTitle = computed(() => {
  const doctor = doctorStation.value.doctorName
  return doctor ? `Registro de Atividade · Jornada — ${doctor}` : 'Registro de Atividade · Jornada'
})

const headerFacts = computed(() => {
  const facts: Array<{ label: string; value: string }> = [
    { label: 'Tipo', value: activityTypeLabel.value },
    { label: 'Pernas', value: COVERED_LEGS_LABELS[coveredLegs.value] ?? '—' },
    { label: 'Anexos', value: String(attachments.value.length) }
  ]
  if (detail.value?.previousContainerId) {
    facts.push({ label: 'Referencia', value: `#${detail.value.previousContainerId}` })
  }
  return facts
})

const riskFlagLabels = computed(() => {
  const flags = detail.value?.activityRiskFlags
  if (!flags) return []
  return (Object.keys(ACTIVITY_RISK_FLAG_LABELS) as ActivityRiskFlag[])
    .filter((key) => flags[key])
    .map((key) => ACTIVITY_RISK_FLAG_LABELS[key])
})

/** Separador dos dados compostos do painel — o mesmo do mockup de M3. */
const DOT = ' · '

function joinFacts(parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(DOT)
}

function stationWindowRow(station: ActivityAuditDoctorStation): string {
  const expected = [
    toHHmm(station.expectedStart, station.timezone || DEFAULT_UTC_OFFSET),
    toHHmm(station.expectedEnd, station.timezone || DEFAULT_UTC_OFFSET)
  ]
    .filter(Boolean)
    .join('–')
  return joinFacts([formatDate(station.date), expected && `${expected} (previsto)`])
}

function toleranceRow(policy: ActivityAuditPolicy): string {
  return joinFacts([
    policy.checkinLateToleranceMinutes != null &&
      `atraso ${policy.checkinLateToleranceMinutes} min`,
    policy.checkoutEarlyToleranceMinutes != null &&
      `saída antecipada ${policy.checkoutEarlyToleranceMinutes} min`
  ])
}

const stationRows = computed(() => {
  const station = doctorStation.value
  const policy = detail.value?.policy ?? {}
  const status = detail.value?.effectiveNow?.registrationStatus
  const tolerances = toleranceRow(policy)

  const rows: Array<{ label: string; value: string }> = [
    {
      label: 'Médico',
      value:
        joinFacts([station.doctorName, station.doctorCrm && `CRM ${station.doctorCrm}`]) ||
        NOT_INFORMED
    },
    { label: 'Plantão', value: stationWindowRow(station) || NOT_INFORMED },
    {
      label: 'Unidade / Escala',
      value:
        joinFacts([station.healthUnitName, station.scheduleName, station.shiftName]) || NOT_INFORMED
    }
  ]
  if (tolerances) rows.push({ label: 'Tolerâncias', value: tolerances })
  if (status) {
    rows.push({
      label: 'Estado hoje',
      value:
        EFFECTIVE_REGISTRATION_STATUS_LABELS[status as EffectiveRegistrationStatus] ??
        String(status)
    })
  }
  return rows
})

/** Perna sem nenhuma fonte vencedora — o texto é o mesmo no "já tem" e na prévia. */
const NO_RECORD = 'Sem registro'

function sourceLabel(source?: ActivityAuditSource): string {
  const kind = sourceKind(source)
  if (!kind) return sourceServerLabel(source) ?? NO_RECORD
  return (
    EFFECTIVE_SOURCE_KIND_LABELS[kind as EffectiveSourceKind] ?? sourceServerLabel(source) ?? kind
  )
}

const effectiveNowRows = computed(() =>
  (['checkin', 'checkout'] as LegKey[]).map((key) => {
    const leg = detail.value?.effectiveNow?.[key] ?? null
    return {
      key,
      label: key === 'checkin' ? 'Check-in' : 'Check-out',
      value: toHHmm(leg?.value, scheduleOffset.value) || '--:--',
      source: leg?.value ? sourceLabel(leg.source) : NO_RECORD
    }
  })
)

const declaredRows = computed(() => {
  const rows: Array<{ label: string; value: string }> = [
    { label: 'Tipo', value: activityTypeLabel.value },
    { label: 'Pernas', value: COVERED_LEGS_LABELS[coveredLegs.value] ?? '—' }
  ]
  if (isLegCovered('checkin')) {
    rows.push({ label: 'Check-in', value: declaredCheckin.value || '--:--' })
  }
  if (isLegCovered('checkout')) {
    rows.push({ label: 'Check-out', value: declaredCheckout.value || '--:--' })
  }
  if (declared.value.activityDescription) {
    rows.push({ label: 'Observação', value: declared.value.activityDescription })
  }
  const sender = joinFacts([
    declared.value.submittedBy?.name,
    declared.value.submittedAt && formatDate(declared.value.submittedAt)
  ])
  if (sender) rows.push({ label: 'Enviado por', value: sender })
  return rows
})

/** Texto da perna que este registro não cobre — e a fonte que continua valendo nela. */
function notCoveredText(leg: LegKey): string {
  const current = detail.value?.effectiveNow?.[leg] ?? null
  const value = toHHmm(current?.value, scheduleOffset.value)
  if (!value) return 'Não faz parte deste registro — sem registro nesta perna'
  return `Não faz parte deste registro — mantém ${value} (${sourceLabel(current?.source)})`
}

const auditorLegs = computed(() =>
  (['checkin', 'checkout'] as LegKey[]).map((key) => {
    const covered = isLegCovered(key)
    const declaredValue = key === 'checkin' ? declaredCheckin.value : declaredCheckout.value
    const currentValue = key === 'checkin' ? auditorCheckin.value : auditorCheckout.value
    return {
      key,
      covered,
      label: key === 'checkin' ? 'Check-in' : 'Check-out',
      declared: declaredValue,
      changed: covered && currentValue !== declaredValue,
      notCoveredText: notCoveredText(key)
    }
  })
)

function onAuditorInput(leg: LegKey, event: Event): void {
  const value = (event.target as HTMLInputElement).value
  if (leg === 'checkin') auditorCheckin.value = value
  else auditorCheckout.value = value
}

const auditorChangedDeclared = computed(() =>
  auditorLegs.value.some((leg) => leg.covered && leg.changed)
)

/**
 * Divergência contra o que está GRAVADO — o gatilho do PATCH. É outra pergunta que a de
 * cima: "confirmei o declarado" pode significar "desfiz a minha própria sobrescrita", e
 * nesse caso o backend PRECISA ser avisado.
 */
function legChangedSincePersisted(key: LegKey): boolean {
  if (!isLegCovered(key)) return false
  const current = key === 'checkin' ? auditorCheckin.value : auditorCheckout.value
  const persisted = key === 'checkin' ? persistedCheckin.value : persistedCheckout.value
  return current !== persisted
}

const auditorChangedPersisted = computed(() =>
  (['checkin', 'checkout'] as LegKey[]).some(legChangedSincePersisted)
)

/**
 * Achado Opus M3 (revisão do W-N3): perna que MUDOU desde o valor persistido, mas sem
 * `declared`/`expected` para `withTime` reconstruir a data (extremo raro). Antes disto,
 * `persistAuditorValues` OMITIA a chave e seguia aprovando — o horário digitado nunca
 * viajava, `auditorOverrideReason` gravava órfão, e `persistedCheckin`/`persistedCheckout`
 * mentiam que o valor tinha sido salvo. Bloquear "Aprovar" é o certo: nem apagar, nem
 * aprovar calado.
 */
const hasUncomputableLegChange = computed(() =>
  (['checkin', 'checkout'] as LegKey[]).some((key) => {
    if (!legChangedSincePersisted(key)) return false
    const currentValue = key === 'checkin' ? auditorCheckin.value : auditorCheckout.value
    return withTime(key, currentValue) === null
  })
)

const hasEmptyCoveredLeg = computed(() =>
  auditorLegs.value.some(
    (leg) => leg.covered && !(leg.key === 'checkin' ? auditorCheckin.value : auditorCheckout.value)
  )
)

/**
 * Terceira validação de §3.4 regra 2: `checkoutReal > checkinReal`. A comparação é feita
 * sobre os MESMOS instantes que o PATCH vai mandar (`withTime` preserva a data original de
 * cada perna), e não sobre `HH:mm` — num plantão que vira o dia, 07:00 depois de 19:00 é
 * perfeitamente válido e um teste lexicográfico o reprovaria.
 */
const legsOutOfOrder = computed(() => {
  if (!isLegCovered('checkin') || !isLegCovered('checkout')) return false
  if (!auditorCheckin.value || !auditorCheckout.value) return false
  const checkin = new Date(withTime('checkin', auditorCheckin.value) ?? '')
  const checkout = new Date(withTime('checkout', auditorCheckout.value) ?? '')
  if (Number.isNaN(checkin.getTime()) || Number.isNaN(checkout.getTime())) return false
  return checkout.getTime() <= checkin.getTime()
})

/**
 * Espelho da validação do backend (§3.4), NUNCA substituto dela: o backend
 * recusa o PATCH e o approve sem motivo. Aqui é só ergonomia — o auditor
 * descobre o requisito antes de clicar, não depois do 400.
 */
const canApprove = computed(
  () =>
    canDecide.value &&
    !hasEmptyCoveredLeg.value &&
    !legsOutOfOrder.value &&
    !hasUncomputableLegChange.value &&
    (!auditorChangedDeclared.value ||
      overrideReason.value.trim().length >= ACTIVITY_DESCRIPTION_MIN_LENGTH)
)

/** `aria-invalid` do textarea de motivo — a mesma régua que desabilita "Aprovar". */
const isOverrideReasonMissing = computed(
  () =>
    auditorChangedDeclared.value &&
    overrideReason.value.trim().length < ACTIVITY_DESCRIPTION_MIN_LENGTH
)

const approveBlockedReason = computed(() => {
  if (!canDecide.value) return 'Este registro já foi decidido.'
  if (hasEmptyCoveredLeg.value) return 'Preencha o horário de todas as pernas deste registro.'
  if (legsOutOfOrder.value) return 'O check-out precisa ser posterior ao check-in.'
  if (hasUncomputableLegChange.value) {
    return 'Não foi possível calcular a data de referência deste horário.'
  }
  if (!canApprove.value) {
    return `Informe o motivo da alteração (mínimo ${ACTIVITY_DESCRIPTION_MIN_LENGTH} caracteres).`
  }
  return ''
})

/* ------------------------------------------------------ veredito já dado (somente leitura) */

const decisionTitle = computed(() =>
  currentStatus.value === DSAttendanceStatus.APPROVED
    ? 'Aprovado — somente leitura'
    : 'Reprovado — somente leitura'
)

const decisionRows = computed(() => {
  const rows: Array<{ label: string; value: string }> = []
  const reason = detail.value?.rejectionReason
  if (reason) {
    rows.push({
      label: 'Motivo',
      value: ACTIVITY_REJECTION_REASON_LABELS[reason as ActivityRejectionReason] ?? String(reason)
    })
  }
  const observation = detail.value?.rejectionJustification ?? detail.value?.justification
  if (observation) rows.push({ label: 'Observação', value: observation })
  const reviewer = joinFacts([
    detail.value?.reviewedBy?.name,
    detail.value?.reviewedAt && formatDate(detail.value.reviewedAt)
  ])
  if (reviewer) rows.push({ label: 'Decidido por', value: reviewer })
  if (!rows.length) rows.push({ label: 'Motivo', value: 'Não informado' })
  return rows
})

/* ---------------------------------------------------------------- pós-plantão ------ */

const isPostStationClosed = computed(() => detail.value?.postStation?.status === 'APPROVED')

const frozenAtSuffix = computed(() => {
  const frozenAt = detail.value?.postStation?.frozenAt
  return frozenAt ? ` em ${formatDate(frozenAt)}` : ''
})

/* ------------------------------------------------------------------- anexos -------- */

const attachments = computed<ActivityAuditAttachment[]>(() => detail.value?.attachments ?? [])

const currentAttachment = computed<ActivityAuditAttachment | null>(
  () => attachments.value[attachmentIndex.value] ?? null
)

const attachmentCaption = computed(() => {
  if (!attachments.value.length) return 'Sem anexos'
  return joinFacts([
    `anexo ${attachmentIndex.value + 1} de ${attachments.value.length}`,
    currentAttachment.value?.originalFileName,
    // Instante real (`evidenceUploadedAt` da fila), lido no fuso da escala como o resto.
    currentAttachment.value?.uploadedAt &&
      `enviado ${formatDateInOffset(currentAttachment.value.uploadedAt, scheduleOffset.value) ?? ''}`
  ])
})

/**
 * Mime que decide o visualizador: o campo do DTO (`ActivityAuditAttachment.mimeType`, se
 * algum dia o backend mandar) vence; senão o `Content-Type` real da resposta do download
 * (`attachmentMimeType`, capturado do `Blob` em `loadAttachment`). Regra do nome do arquivo
 * como último recurso: ver `classifyAttachmentViewer`.
 */
const attachmentKind = computed(() =>
  classifyAttachmentViewer(
    currentAttachment.value?.mimeType ?? attachmentMimeType.value,
    currentAttachment.value?.originalFileName
  )
)

/** `'other'` também cobre a imagem que falhou ao carregar — nunca `<img>` quebrada. */
const viewerMode = computed<'image' | 'pdf' | 'other'>(() => {
  if (attachmentKind.value === 'image') return imageLoadFailed.value ? 'other' : 'image'
  return attachmentKind.value
})

const attachmentType = computed(() => (viewerMode.value === 'pdf' ? 'application/pdf' : undefined))

function releaseAttachmentUrl(): void {
  if (attachmentUrl.value?.startsWith('blob:')) URL.revokeObjectURL(attachmentUrl.value)
  attachmentUrl.value = undefined
}

function handleAttachmentImageError(): void {
  imageLoadFailed.value = true
}

/**
 * Botão de download do anexo que o `StepPreview` não sabe exibir ("outro" tipo, ou imagem
 * que falhou ao carregar) — mesmo padrão do `StepFile.vue` do modal de upload: um link
 * temporário com `download` apontando para o próprio blob já baixado, sem nova requisição.
 */
function downloadCurrentAttachment(): void {
  if (!attachmentUrl.value) return
  const link = document.createElement('a')
  link.href = attachmentUrl.value
  link.download = currentAttachment.value?.originalFileName || 'anexo'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * O arquivo NUNCA vem por URL assinada do storage: sai do proxy autenticado, que carimba a
 * marca d'água de quem está vendo (§9.3). Por isso é blob, e por isso a falha aqui é um
 * toast de verdade — devolver um visualizador vazio esconderia um problema de permissão.
 */
async function loadAttachment(): Promise<void> {
  const attachment = currentAttachment.value
  releaseAttachmentUrl()
  attachmentMimeType.value = undefined
  imageLoadFailed.value = false
  if (!attachment) {
    isLoadingAttachment.value = false
    return
  }
  isLoadingAttachment.value = true
  try {
    const blob = await Network.dsAttendance.getAttachmentFile(
      props.containerId,
      attachment.evidenceId
    )
    // O `Blob` de um download com `responseType: 'blob'` já carrega o `Content-Type` real
    // da resposta em `.type` — nenhuma requisição extra nem header manual precisa disso.
    attachmentMimeType.value = blob.type || undefined
    attachmentUrl.value = URL.createObjectURL(blob)
  } catch (error) {
    console.error('Erro ao abrir o anexo do Registro de Atividade:', error)
    notifyError(error, 'Não foi possível abrir o anexo.')
  } finally {
    isLoadingAttachment.value = false
  }
}

function selectAttachment(index: number): void {
  attachmentIndex.value = index
  rotation.value = 0
  loadAttachment()
}

/* --------------------------------------------------------------- prévia do efetivo - */

/**
 * Contrato de §3.4 — `{ before, after, changedLegs, precedence, frozen, afterClosure }`, com
 * TUDO opcional pela mesma razão do detalhe: o backend da F2 ainda não existe e a tela não
 * pode quebrar por um campo a menos.
 */
interface PreviewSide {
  checkin?: string | null
  checkout?: string | null
  /** Objeto `{kind, label, method}` no contrato final; escalar nas fixtures antigas. */
  checkinSource?: ActivityAuditSource
  checkoutSource?: ActivityAuditSource
  registrationStatus?: EffectiveRegistrationStatus | string | null
}

interface PreviewPrecedenceItem {
  rank?: number | null
  kind?: EffectiveSourceKind | string | null
  label?: string | null
  winsLeg?: Array<'CHECKIN' | 'CHECKOUT' | string> | null
}

interface PreviewEnvelope {
  before?: PreviewSide | null
  after?: PreviewSide | null
  changedLegs?: Array<'CHECKIN' | 'CHECKOUT' | string> | null
  precedence?: PreviewPrecedenceItem[] | null
  /** Top-level do envelope (B17 §1.1 rota 5); carrega o mesmo valor do `after`. */
  registrationStatus?: EffectiveRegistrationStatus | string | null
  frozen?: boolean | null
  afterClosure?: boolean | null
}

const effectivePreview = ref<PreviewEnvelope | null>(null)
/** Distingue "a prévia respondeu e nada muda" de "a prévia não respondeu" (I8). */
const isPreviewUnavailable = ref(false)

/**
 * Duas formas cabem aqui de propósito: o envelope de §3.4 (o contrato que o backend da F2
 * vai servir) e a lista `EffectivePreview[]` que a camada de rede desta branch devolve
 * enquanto o service não é realinhado. Sem o narrowing, a resposta spec'ada viraria `[]` e o
 * bloco sumia sem erro nem log — o achado I8 da revisão.
 */
function fromLegacyItems(items: EffectivePreview[]): PreviewEnvelope | null {
  const stationId = doctorStation.value.id
  const item = items.find((entry) => entry.doctorStationId === stationId) ?? items[0]
  if (!item) return null
  return {
    after: {
      checkin: item.checkin?.value ?? null,
      checkout: item.checkout?.value ?? null,
      checkinSource: legItemSource(item.checkin),
      checkoutSource: legItemSource(item.checkout)
    }
  }
}

/**
 * A fonte de `items[]` é um OBJETO (`{kind, method, label}`) no contrato final, e era um
 * escalar na forma que a W1 supôs. Sem esta leitura, o rótulo da perna viraria
 * "[object Object]" no dia em que a lista chegasse pelo caminho antigo.
 */
function legItemSource(leg?: EffectivePreviewLeg | null): string | null {
  const source = leg?.source as ActivityAuditSource | undefined
  return sourceKind(source) ?? sourceServerLabel(source)
}

function normalizePreview(raw: unknown): PreviewEnvelope | null {
  if (!raw) return null
  if (Array.isArray(raw)) return fromLegacyItems(raw as EffectivePreview[])
  const envelope = raw as PreviewEnvelope
  const hasShape = 'after' in envelope || 'before' in envelope || 'precedence' in envelope
  return hasShape ? envelope : null
}

/**
 * A rejeição é engolida: a prévia é informação de apoio e a decisão do auditor não depende
 * dela — um toast vermelho a cada abertura seria pior. O que NÃO pode acontecer é o bloco
 * desaparecer calado, e é para isso que serve `isPreviewUnavailable`.
 */
async function loadEffectivePreview(): Promise<void> {
  // `effective-preview` é só de auditoria (403 para quem envia): em leitura a
  // seção some, sem "Prévia indisponível" — não há decisão para apoiar.
  if (!canAuditAttendance.value) {
    effectivePreview.value = null
    isPreviewUnavailable.value = false
    return
  }
  try {
    // `checkin`/`checkout` são os nomes da rota (B17 §1.1 rota 5): é o valor que o
    // auditor está digitando que a prévia simula, não o que já está gravado.
    const raw = (await Network.dsAttendance.getEffectivePreview(props.containerId, {
      checkin: isLegCovered('checkin') ? withTime('checkin', auditorCheckin.value) : null,
      checkout: isLegCovered('checkout') ? withTime('checkout', auditorCheckout.value) : null
    })) as unknown
    effectivePreview.value = normalizePreview(raw)
  } catch (error) {
    console.debug('[activity-audit] prévia do efetivo indisponível:', error)
    effectivePreview.value = null
  } finally {
    isPreviewUnavailable.value = effectivePreview.value === null
  }
}

const LEG_TO_PREVIEW_KEY: Record<LegKey, 'CHECKIN' | 'CHECKOUT'> = {
  checkin: 'CHECKIN',
  checkout: 'CHECKOUT'
}

/** Perna do `before`/`after`, que §3.4 achata em `checkin` + `checkinSource`. */
function previewLeg(side: PreviewSide | null | undefined, key: LegKey) {
  const value = key === 'checkin' ? side?.checkin : side?.checkout
  const source = key === 'checkin' ? side?.checkinSource : side?.checkoutSource
  return { value: value ?? null, source: (source ?? null) as ActivityAuditSource }
}

const previewRows = computed(() => {
  const changed = effectivePreview.value?.changedLegs ?? null
  return (['checkin', 'checkout'] as LegKey[]).map((key) => {
    const after = previewLeg(effectivePreview.value?.after, key)
    // Sem `before` (resposta antiga), o "antes" é o que a própria tela já mostra como
    // efetivo de hoje — a mesma fonte do bloco "O que o sistema já tem".
    const before = effectivePreview.value?.before
      ? previewLeg(effectivePreview.value.before, key)
      : {
          value: detail.value?.effectiveNow?.[key]?.value ?? null,
          source: detail.value?.effectiveNow?.[key]?.source ?? null
        }
    const afterValue = toHHmm(after.value, scheduleOffset.value)
    const beforeValue = toHHmm(before.value, scheduleOffset.value)
    return {
      key,
      label: key === 'checkin' ? 'Check-in' : 'Check-out',
      beforeValue: beforeValue || '--:--',
      beforeSource: before.value ? precedenceLabel(before.source) : NO_RECORD,
      afterValue: afterValue || '--:--',
      afterSource: after.value ? precedenceLabel(after.source) : NO_RECORD,
      changed: changed ? changed.includes(LEG_TO_PREVIEW_KEY[key]) : beforeValue !== afterValue
    }
  })
})

const previewAfterClosure = computed(
  () => effectivePreview.value?.afterClosure === true || effectivePreview.value?.frozen === true
)

/**
 * Estado do plantão na prévia: o `registrationStatus` do `after` quando o backend o manda;
 * na resposta antiga, derivado do que a própria prévia mostra — a mesma regra de M1-1
 * (duas pernas = OK).
 */
const previewRegistrationStatusLabel = computed(() => {
  const envelope = effectivePreview.value
  const fromBackend = envelope?.registrationStatus ?? envelope?.after?.registrationStatus
  if (fromBackend) {
    return (
      EFFECTIVE_REGISTRATION_STATUS_LABELS[fromBackend as EffectiveRegistrationStatus] ??
      String(fromBackend)
    )
  }
  const filled = previewRows.value.filter((row) => row.afterValue !== '--:--').length
  const status =
    filled === 2
      ? EffectiveRegistrationStatus.OK
      : filled === 1
        ? EffectiveRegistrationStatus.PARTIAL
        : EffectiveRegistrationStatus.NONE
  return EFFECTIVE_REGISTRATION_STATUS_LABELS[status]
})

/**
 * Rótulo da fonte DENTRO do ranking e da prévia. Diverge de propósito do
 * `EFFECTIVE_SOURCE_KIND_LABELS`: no card de M1 a fonte é "Registro de Atividade" (o estado
 * "aprovado" já aparece ao lado); aqui, onde o auditor compara fontes que disputam a perna,
 * o rótulo de M3/§3.4 é "Registro de Atividade aprovado" — é o que distingue um registro
 * que já venceu a disputa de um que ainda está sendo auditado.
 */
const PRECEDENCE_LABELS: Record<EffectiveSourceKind, string> = {
  ...EFFECTIVE_SOURCE_KIND_LABELS,
  [EffectiveSourceKind.ACTIVITY_RECORD]: 'Registro de Atividade aprovado'
}

/**
 * A fonte chega como OBJETO (`{kind, label, method}`) no `before`/`after` do envelope
 * real — interpolá-la direto era o `antes: [object Object]` do achado R2-I1. O rótulo
 * local pelo `kind` tem precedência; o `label` do servidor cobre um kind desconhecido.
 */
function precedenceLabel(source?: ActivityAuditSource): string {
  const kind = sourceKind(source)
  if (!kind) return sourceServerLabel(source) ?? NO_RECORD
  return PRECEDENCE_LABELS[kind as EffectiveSourceKind] ?? sourceServerLabel(source) ?? kind
}

/**
 * Ordem padrão do ranking = ordem de declaração do enum (o `DEFAULT_SOURCE_PRECEDENCE` do
 * backend). `LEGACY` fica fora: é o balde de compatibilidade da F1, não uma fonte que
 * dispute perna nenhuma.
 */
const DEFAULT_PRECEDENCE: EffectiveSourceKind[] = [
  EffectiveSourceKind.SHEET,
  EffectiveSourceKind.APP,
  EffectiveSourceKind.EXTERNAL_APP,
  EffectiveSourceKind.ACTIVITY_RECORD,
  EffectiveSourceKind.AUTO_PERMANENCE,
  EffectiveSourceKind.MANUAL_LEGACY
]

const PREVIEW_LEG_LABELS: Record<string, string> = {
  CHECKIN: 'check-in',
  CHECKOUT: 'check-out'
}

/**
 * §3.4 devolve `precedence[]` pronto — rank, rótulo e as pernas que cada fonte vence. Quando
 * ele vem, é ele que manda (I11): recalcular no cliente cria uma segunda fonte de verdade
 * que diverge no primeiro `EffectiveSourceKind` novo. O cálculo local fica só como fallback
 * para a resposta antiga, que não traz o ranking.
 */
const precedenceRanking = computed(() => {
  const fromBackend = effectivePreview.value?.precedence
  if (Array.isArray(fromBackend) && fromBackend.length) {
    return fromBackend.map((item, index) => {
      const legs = (item.winsLeg ?? []).map((leg) => PREVIEW_LEG_LABELS[leg] ?? String(leg))
      return {
        kind: String(item.kind ?? index),
        rank: item.rank ?? index + 1,
        label: item.label || precedenceLabel(item.kind),
        wins: legs.length > 0,
        winsHint: `vence o ${legs.join(' e o ')}`
      }
    })
  }

  const configured = detail.value?.policy?.sourcePrecedence
  const order =
    Array.isArray(configured) && configured.length
      ? (configured as EffectiveSourceKind[])
      : DEFAULT_PRECEDENCE

  const winningLegs = new Map<string, string[]>()
  previewRows.value.forEach((row) => {
    const source = sourceKind(previewLeg(effectivePreview.value?.after, row.key).source)
    if (!source) return
    const legs = winningLegs.get(source) ?? []
    legs.push(row.label.toLowerCase())
    winningLegs.set(source, legs)
  })

  return order.map((kind, index) => {
    const legs = winningLegs.get(String(kind)) ?? []
    return {
      kind: String(kind),
      rank: index + 1,
      label: precedenceLabel(kind),
      wins: legs.length > 0,
      winsHint: `vence o ${legs.join(' e o ')}`
    }
  })
})

/* ------------------------------------------------------------------- rejeição ------ */

/**
 * Catálogo local e fechado (§3.6). O `getRejectionReasons` do service só sabe filtrar por
 * `monitoringType`, que em `ATIVIDADE` é nulo — o enum daqui é espelho byte a byte do do
 * backend, então a lista é determinística e não depende de uma rota que ainda não existe.
 */
const rejectionReasonOptions = Object.values(ActivityRejectionReason).map((value) => ({
  value,
  title: ACTIVITY_REJECTION_REASON_LABELS[value]
}))

const isOtherRejectionReason = computed(
  () => selectedRejectionReason.value === ActivityRejectionReason.OTHER
)

const canReject = computed(() => {
  if (!canDecide.value) return false
  if (!selectedRejectionReason.value) return false
  if (!isOtherRejectionReason.value) return true
  return rejectionObservation.value.trim().length >= ACTIVITY_DESCRIPTION_MIN_LENGTH
})

/* --------------------------------------------------------------------- carga ------- */

const entryId = computed(() => detail.value?.auditor?.entryId ?? detail.value?.entryId ?? null)

/**
 * A semeadura dos campos dispara o `watch` que recarrega a prévia; sem esta trava a tela
 * pediria `effective-preview` duas vezes em toda abertura (uma no `loadAll`, outra no
 * watcher) com exatamente os mesmos parâmetros.
 */
let skipNextPreviewWatch = false

function seedAuditorFields(): void {
  // O campo do auditor NASCE com o declarado (M3 balão ⑦): a decisão mais comum é
  // confirmar, e um campo vazio transformaria "concordo" em digitação.
  skipNextPreviewWatch = true
  auditorCheckin.value =
    toHHmm(detail.value?.auditor?.checkin, scheduleOffset.value) || declaredCheckin.value
  auditorCheckout.value =
    toHHmm(detail.value?.auditor?.checkout, scheduleOffset.value) || declaredCheckout.value
  overrideReason.value = detail.value?.auditor?.overrideReason ?? ''
  // O que o backend TEM gravado hoje — que não é o declarado quando o auditor já
  // sobrescreveu numa sessão anterior. É contra este valor que o PATCH é decidido.
  persistedCheckin.value = auditorCheckin.value
  persistedCheckout.value = auditorCheckout.value
}

/**
 * R2-C2 — `GET containers/:id` devolve o container ACHATADO (`kind`, `entryId`,
 * `doctorStationId`, `timezone`, unidade, escala, emissor) e nada do declarado: sem esta
 * costura a tela abria com "Não informado" em tudo, `Anexos: 0` e `--:--`. O declarado, os
 * anexos e o efetivo vivem em `GET containers/:id/activity-record` (BF11) — o recorte da
 * registration do plantão servido sob o guard do container, e não o da escala: a
 * registration exige leitura da ESCALA e o auditor comum tomava 403 (a tela abria vazia).
 * Médico e sinais de risco vêm do item da fila; o valor já gravado pelo auditor, da entry.
 * As três chamadas são em paralelo e cada uma é best-effort: a que falhar apaga só a sua
 * parte, nunca a tela.
 */
async function hydrateDetail(raw: ActivityContainerDetailSource): Promise<ActivityAuditDetail> {
  const doctorStationId = raw.doctorStationId ?? null
  const [activityRecord, listing, entries] = await Promise.allSettled([
    Network.dsAttendance.getActivityRecord(props.containerId),
    doctorStationId
      ? Network.dsAttendance.listByCompany({
          kind: ContainerKind.ATIVIDADE,
          doctorStationId,
          page: 1,
          limit: 50
        })
      : null,
    Network.dsAttendance.getContainerEntries(props.containerId)
  ])

  reportHydrationFailures({ activityRecord, listing, entries })

  const items = settled(listing)?.data ?? []
  const listItem =
    (items as ActivityListItemSource[]).find((item) => Number(item.id) === Number(raw.id)) ?? null

  return buildActivityAuditDetail({
    detail: raw,
    activityRecord: settled(activityRecord) as ActivityRegistrationSource | null,
    listItem,
    entry: settled(entries)?.[0] ?? null
  })
}

function settled<T>(result: PromiseSettledResult<T>): T | null {
  return result.status === 'fulfilled' ? result.value : null
}

function reportHydrationFailures(results: {
  activityRecord: PromiseSettledResult<unknown>
  listing: PromiseSettledResult<unknown>
  entries: PromiseSettledResult<unknown>
}): void {
  if (results.activityRecord.status === 'rejected') {
    // Sem o activity-record (403/404, rota ainda não deployada) o auditor fica sem
    // declarado e sem anexo — isso merece um toast de verdade, mas não um bloqueio: o que
    // veio do container e da fila continua na tela.
    const reason = results.activityRecord.reason
    console.error('Erro ao carregar o activity-record do container para a auditoria:', reason)
    notifyError(reason, 'Não foi possível carregar o declarado deste registro.')
  }
  if (results.listing.status === 'rejected') {
    console.debug('[activity-audit] item da fila indisponível; sem médico/risco', results.listing)
  }
  if (results.entries.status === 'rejected') {
    console.debug(
      '[activity-audit] entry indisponível; auditor nasce com o declarado',
      results.entries
    )
  }
}

async function resolveDetail(raw: unknown): Promise<ActivityAuditDetail> {
  if (isActivityAuditDetailShape(raw)) return raw
  return hydrateDetail((raw ?? {}) as ActivityContainerDetailSource)
}

/**
 * `fromServer` existe para UM caso: a recarga depois de uma decisão recusada. Na abertura
 * da tela o retrato que o hospedeiro já buscou serve; ali, não — `props.containerDetail` é
 * exatamente o estado VELHO que a recusa acabou de desmentir, e reusá-lo deixaria o botão
 * habilitado convidando ao mesmo clique que falhou.
 */
async function loadDetail(options: { fromServer?: boolean } = {}): Promise<boolean> {
  try {
    // O hospedeiro já pagou o `getById` para descobrir o `kind` — repetir seria duas
    // consultas idênticas por abertura, uma delas capaz de divergir da outra.
    const cached = options.fromServer ? null : props.containerDetail
    const raw = cached ?? (await Network.dsAttendance.getById(props.containerId))
    detail.value = await resolveDetail(raw)
    seedAuditorFields()
    return true
  } catch (error) {
    console.error('Erro ao buscar o Registro de Atividade:', error)
    const fallback = 'Não foi possível carregar o Registro de Atividade.'
    loadErrorMessage.value = getApiErrorMessage(error, fallback)
    notifyError(error, fallback)
    return false
  } finally {
    isLoadingDetail.value = false
  }
}

async function loadHistory(): Promise<void> {
  try {
    const response = await Network.dsAttendance.getAuditTrail(props.containerId)
    historyEvents.value = mapAuditTrailToHistoryEvents(response.data)
  } catch (error) {
    console.error('Erro ao buscar o histórico do Registro de Atividade:', error)
    notifyError(error, 'Não foi possível carregar o histórico do registro.')
  } finally {
    isLoadingHistory.value = false
  }
}

async function loadAll(): Promise<void> {
  const loaded = await loadDetail()
  if (!loaded) {
    hasLoadError.value = true
    isLoadingAttachment.value = false
    isLoadingHistory.value = false
    return
  }
  hasLoadError.value = false
  await Promise.allSettled([loadHistory(), loadAttachment(), loadEffectivePreview()])
}

/* --------------------------------------------------------------------- ações ------- */

function goToApprovals(): void {
  router.push(approvalsQueueLocation('attendance'))
}

/**
 * Q22 — no ataque em sequência, a auditoria é uma parada no meio da fila. O
 * `?returnTo=` diz de onde a pessoa veio; sem ele a tela segue exatamente como
 * hoje (o caminho de volta é o breadcrumb "Aprovações ›").
 *
 * O destino é validado: `returnTo` vem da URL, e só caminho da própria fila
 * volta — nada de redirect aberto para fora do painel.
 */
const returnToQueuePath = computed<string | null>(() => {
  const value = route.query[PRIORITY_RETURN_TO_PARAM]
  return isPriorityReturnTo(value) ? value : null
})

function goToReturnToQueue(): void {
  if (returnToQueuePath.value) router.push(returnToQueuePath.value)
}

const hasNextContainer = computed(() => nextContainerId.value !== null)

/**
 * Grava o valor do auditor só quando ele difere do que está GRAVADO na entry — não do
 * declarado. A entry nasce com o declarado em `checkinReal`/`checkoutReal` (§1.2), então nos
 * dois casos o resultado é o mesmo enquanto ninguém sobrescreveu; a diferença aparece na
 * reabertura: desfazer a própria sobrescrita voltando ao declarado É uma gravação, e pular o
 * PATCH ali aprovava o horário antigo com a tela mostrando o novo.
 *
 * `auditorOverrideReason` continua indo só quando o valor gravado difere do DECLARADO, que é
 * exatamente a regra que o backend cobra (§3.4 regra 2).
 */
async function persistAuditorValues(): Promise<void> {
  if (!auditorChangedPersisted.value) return
  const id = entryId.value
  if (id === null) {
    throw new Error('Registro sem lançamento vinculado: não é possível gravar o valor do auditor.')
  }
  // R2-C3: só a perna que o auditor MUDOU viaja — a outra fica como está na entry. O
  // valor vai em ISO com o offset do plantão (`withTime` reaproveita a string declarada).
  const nextCheckin = legChangedSincePersisted('checkin')
    ? withTime('checkin', auditorCheckin.value)
    : undefined
  const nextCheckout = legChangedSincePersisted('checkout')
    ? withTime('checkout', auditorCheckout.value)
    : undefined
  await Network.dsAttendance.updateActivityEntry(id, props.containerId, {
    // `withTime` pode devolver `null` (perna sem declarado NEM `expected` — extremo raro).
    // `?? undefined` OMITE o campo nesse caso: o backend só pula chaves `undefined` no
    // PATCH, então mandar `null` de propósito APAGARIA o valor já gravado do auditor
    // (achado Opus W-N3) — "sem data para calcular" nunca pode virar "sem valor". Este
    // ramo já é defensivo: `canApprove` (achado M3, `hasUncomputableLegChange`) bloqueia
    // "Aprovar" ANTES de chegar aqui quando a perna mudou e `withTime` daria `null` — mas
    // manter o `?? undefined` custa nada e evita um `null` explícito se essa invariante
    // algum dia quebrar.
    checkinReal: nextCheckin ?? undefined,
    checkoutReal: nextCheckout ?? undefined,
    // Voltar ao declarado ZERA o motivo (`null`), não o omite: omitir deixava na entry o
    // motivo de uma sobrescrita que não existe mais.
    auditorOverrideReason: auditorChangedDeclared.value ? overrideReason.value.trim() : null
  })
  persistedCheckin.value = auditorCheckin.value
  persistedCheckout.value = auditorCheckout.value
}

/**
 * Toda decisão recusada termina aqui, e o que ela faz é DEVOLVER A TELA À REALIDADE —
 * mas só quando a realidade mudou de fato.
 *
 * Antes, o `catch` só mostrava um toast: o `status` local continuava o antigo, `canDecide`
 * continuava `true` e o botão convidava a um novo clique que ia falhar igual.
 *
 * A recarga é restrita aos dois códigos da máquina de estados (backend #2820) porque ela
 * passa por `seedAuditorFields()` e portanto DESCARTA o que o auditor digitou. Quando a
 * recusa diz que o registro mudou de situação, descartar é o certo: aquele horário já não
 * tem onde ser gravado. Numa falha de rede, num 500 ou numa validação de campo, o registro
 * continua exatamente onde estava e apagar a digitação seria punir o auditor por um
 * problema que não é dele — ali fica o toast, e só.
 *
 * `DS_ATTENDANCE_CONTAINER_ALREADY_IN_STATUS` (409) NÃO é falha: o registro já está no
 * status pedido, ou seja, a ação do auditor já surtiu efeito (clique duplo, outra aba,
 * outro auditor). Vermelho ali ensinaria a desconfiar de um sucesso — vai de `toast.info`
 * com a mensagem do backend, que é quem sabe quando aconteceu.
 *
 * `ACTIVITY_REJECTION_SUPERSEDED` (também 409) É falha: a reversão da reprovação não cabe
 * porque o emissor já reenviou um registro novo. Erro vermelho com a frase do backend (que
 * manda auditar o sucessor) — ou, se ela faltar, a dica canônica com a mesma instrução — e
 * recarga, porque a situação deste registro mudou por fora. Os três são distinguidos pelo
 * CÓDIGO, nunca pelo status HTTP — dois deles dividem o 409.
 */
async function handleDecisionFailure(error: unknown, fallback: string): Promise<void> {
  const isAlreadyInStatus = hasApiErrorCode(error, DS_ATTENDANCE_CONTAINER_ALREADY_IN_STATUS)
  const isInvalidTransition = hasApiErrorCode(error, DS_ATTENDANCE_CONTAINER_INVALID_TRANSITION)
  const isRejectionSuperseded = isActivityRejectionSupersededError(error)

  if (isAlreadyInStatus) {
    toast.info(
      getApiErrorMessage(
        error,
        'Este registro já está nesta situação. A tela foi atualizada com a situação atual.'
      )
    )
  } else if (isInvalidTransition) {
    notifyError(error, 'Esta ação não cabe mais na situação atual do registro.')
  } else if (isRejectionSuperseded) {
    notifyError(error, ACTIVITY_REJECTION_SUPERSEDED_FALLBACK_MESSAGE)
  } else {
    notifyError(error, fallback)
  }

  if (isAlreadyInStatus || isInvalidTransition || isRejectionSuperseded) {
    await loadDetail({ fromServer: true })
  }
}

// Spec 2026-09-24 §8.1 — aprovar passa pela prévia da sobrescrita de perna.
const legOverwrite = useLegOverwriteApproval()
const {
  isOpen: isLegOverwriteOpen,
  conflicts: legOverwriteConflicts,
  effectiveValues: legOverwriteEffectiveValues,
  effectiveValueStatus: legOverwriteEffectiveValueStatus,
  isSubmitting: isLegOverwriteSubmitting,
  errorMessage: legOverwriteError,
  staleNotice: legOverwriteStale
} = legOverwrite

/**
 * `persistAuditorValues` roda ANTES da prévia de propósito: `incoming.time` é o
 * `checkinReal/checkoutReal` já corrigido pelo auditor (spec §6.2). Cancelar a
 * confirmação não desfaz o PATCH — é valor gravado, não aprovação.
 */
async function approve(options: { openNext: boolean }): Promise<void> {
  if (!canApprove.value || isSaving.value) return
  isSaving.value = true
  try {
    await persistAuditorValues()
    const outcome = await legOverwrite.approve(props.containerId, {
      justification: auditorObservation.value.trim() || undefined
    })
    if (outcome === 'CANCELLED') return
    toast.success(
      isPostStationClosed.value
        ? 'Registro aprovado e marcado como recebido após fechamento.'
        : 'Registro de Atividade aprovado.'
    )
    if (options.openNext && (await goToNext(props.containerId, nextContainerId.value))) return
    goToApprovals()
  } catch (error) {
    await handleDecisionFailure(error, 'Não foi possível aprovar o Registro de Atividade.')
  } finally {
    isSaving.value = false
  }
}

async function reject(): Promise<void> {
  if (!canReject.value || isSaving.value) return
  isSaving.value = true
  try {
    await Network.dsAttendance.rejectContainer(props.containerId, {
      reason: selectedRejectionReason.value as string,
      justification: rejectionObservation.value.trim() || undefined,
      // Nada de "solicitar nova folha": não existe reenvio em cima deste container, o
      // emissor manda um registro novo que referencia este (Q20). A volta deste registro
      // para a fila é por `revertValidation`, não por uma nova folha.
      requestNewSheet: false
    })
    toast.success('Registro de Atividade reprovado.')
    isRejectOpen.value = false
    goToApprovals()
  } catch (error) {
    await handleDecisionFailure(error, 'Não foi possível reprovar o Registro de Atividade.')
  } finally {
    isSaving.value = false
  }
}

/**
 * Desfaz o veredito pelo mesmo endpoint da folha. Dois caminhos, porque a rota de cada
 * veredito é diferente:
 *
 * - Aprovação: o registro volta para Auditoria em Progresso e o backend apaga o check que a
 *   aprovação tinha criado no plantão. A tela VOLTA PARA A FILA: a rota aqui é
 *   `DsAttendanceApproved`, e deixar um registro que já não está aprovado num endereço que
 *   promete aprovados é mentir sobre onde a pessoa está (é o que a folha faz também).
 * - Reprovação (PO, 01/10/2026): o registro volta para Aguardando Auditoria e o backend
 *   cancela a solicitação de reenvio que a reprovação tinha aberto. A tela FICA ONDE ESTÁ:
 *   `REJECTED` e `PENDING_AUDIT` abrem na mesma rota (`status-route-map.ts`), então basta
 *   recarregar o detalhe e o histórico — e o auditor já decide no mesmo lugar.
 *
 * O `wasRejected` é capturado ANTES do `await` de propósito: é o status que a tela tinha
 * quando a pessoa clicou, não o que o servidor devolver depois.
 *
 * O `handleDecisionFailure` é o mesmo das decisões porque a máquina de estados é a mesma:
 * reverter duas vezes (clique duplo, outra aba, outro auditor) devolve os códigos que ele
 * já sabe distinguir de uma falha de verdade — inclusive o 409 do reenvio já feito.
 */
async function revertValidation(justification: string): Promise<void> {
  if (!canRevertDecision.value || isSaving.value) return
  const wasRejected = currentStatus.value === DSAttendanceStatus.REJECTED
  isSaving.value = true
  try {
    await Network.dsAttendance.revertValidation(props.containerId, { justification })
    isRevertModalOpen.value = false
    if (!wasRejected) {
      toast.success('Validação revertida com sucesso.')
      goToApprovals()
      return
    }
    toast.success('Reprovação revertida. O registro voltou para auditoria.')
    if (await loadDetail({ fromServer: true })) {
      await Promise.allSettled([loadHistory(), loadEffectivePreview()])
    }
  } catch (error) {
    await handleDecisionFailure(error, 'Não foi possível reverter a validação do registro.')
  } finally {
    isSaving.value = false
  }
}

/**
 * `Ctrl+Enter` = a ação primária, igual à auditoria da folha. Nunca faz o que o clique não
 * faria. A guarda de foco poupa o campo de motivo e a observação, onde `Ctrl+Enter` é quebra
 * de linha para quem digita — o campo de horário fica de fora porque nele o atalho não
 * compete com nada.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.tagName === 'TEXTAREA' ||
    (target.tagName === 'INPUT' && target.getAttribute('type') !== 'time') ||
    target.isContentEditable
  )
}

function handleApproveShortcut(event: KeyboardEvent): void {
  if (event.key !== 'Enter') return
  if (!event.ctrlKey && !event.metaKey) return
  if (isTypingTarget(event.target)) return
  if (!canApprove.value || isSaving.value) return
  event.preventDefault()
  approve({ openNext: hasNextContainer.value })
}

/** `Esc` fecha a lupa — o mesmo gesto do visualizador da folha. */
function handleEscape(event: KeyboardEvent): void {
  if (event.key === 'Escape' && isMagnifierOn.value) isMagnifierOn.value = false
}

/**
 * A prévia é recalculada quando o auditor muda o horário: a pergunta que ela responde é
 * "o que vai valer se eu aprovar ISTO", não "se eu aprovasse o declarado".
 */
watch([auditorCheckin, auditorCheckout], () => {
  if (skipNextPreviewWatch) {
    skipNextPreviewWatch = false
    return
  }
  if (isLoadingDetail.value || hasLoadError.value) return
  loadEffectivePreview()
})

onMounted(() => {
  headerStore.setTitle(
    'Auditoria de Registro de Atividade',
    'Confira o anexo e decida o horário que vale',
    approvalsQueueBreadcrumbs('attendance')
  )
  window.addEventListener('keydown', handleApproveShortcut)
  window.addEventListener('keydown', handleEscape)
})

onMounted(loadAll)

/**
 * O `nextId` é capturado ANTES de qualquer aprovação (correção já em produção na folha):
 * depois de aprovar, este registro saiu da fila e uma nova consulta devolveria o primeiro
 * da fila, não o que estava logo depois deste.
 */
onMounted(async () => {
  nextContainerId.value = await fetchNextId(props.containerId)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleApproveShortcut)
  window.removeEventListener('keydown', handleEscape)
  releaseAttachmentUrl()
})

defineExpose({ rootEl })
</script>

<style scoped></style>
